import AvailableDomainBody from "components/availableDomainBody/AvailableDomainBody";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import PurchaseDropDown from "components/purchaseDropDown/PurchaseDropDown";
import TakenDomainBody from "components/takenDomainBody/TakenDomainBody";
import WhoisButton from "components/whoisButton/WhoisButton";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Spinner, Alert } from "react-bootstrap";
import Helmet from "components/headHelmet/HeadHelmet";
import { Link, useParams } from "react-router-dom";
import domainSlice from "reducers/domainSlice";
import {
  domainDetailsSelector,
  getDomainDetailsErrorSelector,
  gettingDomainDetailsSelector,
  resellersSelector,
  alternativeDomainNamesSelector,
  gettingAlternativeDomainNamesSelector,
} from "selectors/domainSelectors";
import styled from "styled-components";
import { getSiteUrl } from "utility/urlUtility";
import DomainBrandContent from "./DomainBrandContent";
import {
  domainHash,
  hashPick,
  hasText,
  toTitleCase,
  toList,
  CATEGORY_CONNECTORS,
  STRONG_SCORE_PHRASES,
  SOLID_SCORE_PHRASES,
  buildDomainJsonLd,
  getBrandPotentialLabel,
} from "./domainPageUtils";

// ── Styled components ───────────────────────────────────────────────────────

const StyledPageTitle = styled(PageTitle)<{
  $registered: boolean;
  $loaded: boolean;
}>`
  flex: 1;
  margin-bottom: 0;
  color: ${(props: any) =>
    props.$loaded ? (props.$registered ? "#c60404" : "#21b400") : "#222222"};
`;

const Header = styled.div<{ $showBrandContent: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  min-height: 48px;
  margin-bottom: ${(props: any) => (props.$showBrandContent ? "24px" : "10px")};

  @media only screen and (max-width: 992px) {
    padding-top: 10px;
  }

  @media only screen and (max-width: 500px) {
    align-items: flex-start;
  }
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
  order: 3;

  @media only screen and (max-width: 768px) {
    order: 2;
  }
`;

const HeaderBrandPill = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 9px 15px;
  border-radius: 8px;
  background: #ecfdf3;
  font-size: 0.8rem;
  font-weight: 600;
  color: #067a00;
  white-space: nowrap;
  order: 2;
  margin-left: 12px;

  @media only screen and (max-width: 768px) {
    order: 3;
    flex: 1 1 100%;
    margin-left: 0;
    margin-top: 24px;
    justify-content: center;
  }
`;

const SpinnerWrapper = styled.div`
  color: #28c604;
  font-size: initial;

  > div {
    font-size: 1.5rem;
    width: 30px;
    height: 30px;
  }
`;

// ── Component ───────────────────────────────────────────────────────────────

const DomainPage = () => {
  const { domain } = useParams();
  const dispatch = useAppDispatch();

  const [records, setRecords] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    if (domain) dispatch(domainSlice.actions.getDomainDetails(domain));
  }, [dispatch, domain]);

  const gettingDomainDetails = useAppSelector(gettingDomainDetailsSelector);
  const domainDetails = useAppSelector(domainDetailsSelector);
  const getDomainDetailsError = useAppSelector(getDomainDetailsErrorSelector);
  const resellers = useAppSelector(resellersSelector);
  const alternativeDomainNames = useAppSelector(alternativeDomainNamesSelector);
  const gettingAlternativeDomainNames = useAppSelector(
    gettingAlternativeDomainNamesSelector,
  );

  useEffect(() => {
    if (domain && domainDetails?.registered === true) {
      dispatch(domainSlice.actions.getAlternativeDomainNames(domain));
    }
  }, [dispatch, domain, domainDetails?.registered]);

  const handleOnRecodsLoaded = useCallback((r: string[]) => setRecords(r), []);

  const titleLoaded = useMemo(
    () =>
      gettingDomainDetails === false && domainDetails?.registered !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const showBrandContent = useMemo(
    () =>
      titleLoaded &&
      !!(
        domainDetails?.brandScore != null ||
        hasText(domainDetails?.primaryCategory) ||
        hasText(domainDetails?.structureType) ||
        (domainDetails?.semanticTokens?.length ?? 0) > 0
      ),
    [titleLoaded, domainDetails],
  );

  const HeaderActions = useMemo(
    () => (
      <Status>
        {gettingDomainDetails === true && (
          <SpinnerWrapper>
            <Spinner size="sm" />
          </SpinnerWrapper>
        )}

        {domain && !gettingDomainDetails && domainDetails?.registered && (
          <WhoisButton
            records={records}
            name={domain}
            onRecordsLoaded={handleOnRecodsLoaded}
          />
        )}

        {domain &&
          !gettingDomainDetails &&
          !domainDetails?.registered &&
          resellers && (
            <PurchaseDropDown resellers={resellers} domain={domain} />
          )}
      </Status>
    ),
    [
      domain,
      gettingDomainDetails,
      domainDetails,
      resellers,
      records,
      handleOnRecodsLoaded,
    ],
  );

  const DomainBody = useMemo(() => {
    if (!domain || gettingDomainDetails || !domainDetails) return null;
    return domainDetails.registered ? (
      <TakenDomainBody domainDetails={domainDetails} />
    ) : (
      <AvailableDomainBody domainDetails={domainDetails} />
    );
  }, [domain, gettingDomainDetails, domainDetails]);

  const isThinPage = useMemo(
    () =>
      !!domain &&
      domainDetails?.registered === false &&
      (!domainDetails.businessIdeas ||
        domainDetails.businessIdeas.length === 0),
    [domain, domainDetails],
  );

  const detailsLoaded = useMemo(
    () => gettingDomainDetails === false && domainDetails !== undefined,
    [gettingDomainDetails, domainDetails],
  );

  const prerenderedRobots = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document
      .querySelector('meta[name="robots"]')
      ?.getAttribute("content");
  }, []);

  const shouldIndex = useMemo(() => {
    if (!detailsLoaded || !domainDetails) return null;
    if (typeof domainDetails.isIndexable === "boolean") {
      return domainDetails.isIndexable;
    }
    // Backward-compatible fallback while API rolls out isIndexable.
    return !isThinPage;
  }, [detailsLoaded, domainDetails, isThinPage]);

  const robotsContent = useMemo(() => {
    if (shouldIndex === null) return prerenderedRobots ?? "noindex,follow";
    return shouldIndex ? "index,follow" : "noindex,follow";
  }, [shouldIndex, prerenderedRobots]);

  const metaTitle = useMemo(() => {
    if (!domainDetails) return `${domain} - Domain Availability | Domain Findr`;
    return domainDetails.registered
      ? `${domain} - Registered Domain | Domain Findr`
      : `${domain} - Available Domain for Sale | Domain Findr`;
  }, [domain, domainDetails]);

  const metaDescription = useMemo(() => {
    if (!domainDetails)
      return `Check availability and details for ${domain}. Find domain registration info, business ideas, and purchase options.`;

    const h = domainHash(domain ?? "");

    const cat = hasText(domainDetails.primaryCategory)
      ? `${toTitleCase(domainDetails.primaryCategory as string).toLowerCase()}${hashPick(CATEGORY_CONNECTORS, h, 3)}`
      : null;

    const scorePart =
      domainDetails.brandScore != null
        ? domainDetails.brandScore >= 75
          ? hashPick(STRONG_SCORE_PHRASES, h, 4)
          : domainDetails.brandScore >= 60
            ? hashPick(SOLID_SCORE_PHRASES, h, 5)
            : null
        : null;

    const tokens = (domainDetails.semanticTokens ?? [])
      .filter((t) => hasText(t))
      .slice(0, 3)
      .map((t) => t.trim().toLowerCase());
    const semanticPart =
      tokens.length > 0 ? `for ${toList(tokens)} projects` : null;

    const catStr = cat ? ` ${cat}` : "";
    const scoreStr = scorePart ? ` ${scorePart}` : "";
    const semanticStr = semanticPart ? ` ${semanticPart}` : "";

    if (domainDetails.registered) {
      return `${domain} is a registered${catStr} domain${scoreStr}. View WHOIS data, registration details, and ownership information.`;
    }

    const base = `${domain} is an available${catStr} domain${scoreStr}${semanticStr}.`;

    const ideasCount = domainDetails.businessIdeas?.length || 0;
    if (ideasCount > 0) {
      return `${base} Explore ${ideasCount} business idea${
        ideasCount > 1 ? "s" : ""
      } and find the best registrar to buy this domain.`;
    }

    return `${base} Compare prices from top domain registrars and secure this domain today.`;
  }, [domain, domainDetails]);

  const jsonLd = useMemo(() => {
    if (!detailsLoaded || !domainDetails || !domain) return null;
    return buildDomainJsonLd(
      domain,
      domainDetails,
      metaTitle,
      metaDescription,
      getSiteUrl(),
    );
  }, [detailsLoaded, domain, domainDetails, metaTitle, metaDescription]);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`${getSiteUrl()}/domain/${domain}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${getSiteUrl()}/domain/${domain}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta
          property="twitter:url"
          content={`${getSiteUrl()}/domain/${domain}`}
        />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />

        {/* Robots is API-driven (with thin-page fallback) and independent of render mode. */}
        {robotsContent && <meta name="robots" content={robotsContent} />}

        {/* JSON-LD structured data */}
        {jsonLd && <script type="application/ld+json">{jsonLd}</script>}
      </Helmet>

      <Page collapsedView={true} maxWidth={920}>
        <PageWrapper>
          <Header $showBrandContent={showBrandContent}>
            <StyledPageTitle
              $registered={domainDetails?.registered ?? false}
              $loaded={titleLoaded}
              align="left"
            >
              {domain}
            </StyledPageTitle>
            {!gettingDomainDetails && domainDetails?.brandScore != null && (
              <HeaderBrandPill>
                {domainDetails.brandScore}/100 &nbsp;&middot;&nbsp;
                {getBrandPotentialLabel(domainDetails.brandScore)}
              </HeaderBrandPill>
            )}
            {HeaderActions}
          </Header>

          {titleLoaded && domainDetails && (
            <DomainBrandContent
              domainDetails={domainDetails}
              domain={domain}
              alternativeDomainNames={alternativeDomainNames ?? undefined}
              gettingAlternativeDomainNames={gettingAlternativeDomainNames}
            />
          )}

          {getDomainDetailsError && (
            <Alert className="mt-3" variant="danger">
              {getDomainDetailsError}
            </Alert>
          )}

          {DomainBody}
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(DomainPage);
